import { useState, useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppContext } from 'contexts/AppContext';
import { YES_NO_OPTIONS } from 'utils/constants';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';

export const GridProps = {
  xs: 12,
  sm: 6,
};

export const PETS_FIELD_NAMES = {
  name: 'name',
  petType: 'petType',
  breed: 'breed',
  weight: 'weight',
  serviceAnimal: 'serviceAnimal',
  isOpen: 'isOpen',
};

export const NEW_PET = {
  [PETS_FIELD_NAMES.name]: '',
  [PETS_FIELD_NAMES.petType]: '',
  [PETS_FIELD_NAMES.breed]: '',
  [PETS_FIELD_NAMES.weight]: '',
  [PETS_FIELD_NAMES.serviceAnimal]: '',
  [PETS_FIELD_NAMES.isOpen]: true,
};

const usePetsFields = ({ formId, watch }) => {
  const intl = useIntl();
  const { petTypes } = useContext(AppContext);
  const [requiresWeight, setRequiresWeight] = useState(false);
  const selectedPetType = watch(PETS_FIELD_NAMES.petType);

  const petBreeds = useMemo(() => {
    return petTypes?.find(({ id }) => id === selectedPetType)?.petBreeds ?? [];
  }, [selectedPetType, petTypes]);

  useEffect(() => {
    if (selectedPetType) {
      const isWeightRequired = !!petTypes?.find(
        (pt) => pt.id === selectedPetType,
      )?.requiresWeight;
      setRequiresWeight(isWeightRequired);
    }
  }, [selectedPetType, petTypes]);

  const petsFields = useMemo(
    () =>
      [
        {
          name: PETS_FIELD_NAMES.name,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.pets.name',
          }),
          variant: 'text',
          placeholder: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.pets.name',
          }),
          rules: {
            required: true,
            validate: validateNoSpecialChars,
          },
          GridProps: { ...GridProps, marginBottom: 2 },
        },
        {
          name: PETS_FIELD_NAMES.petType,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.pets.petType',
          }),
          variant: 'singleSelect',
          options: petTypes?.map((type) => ({
            text: type.name,
            value: type.id,
          })),
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: PETS_FIELD_NAMES.breed,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.pets.breed',
          }),
          variant: 'singleSelect',
          options: petBreeds?.map((breed) => ({
            text: breed.name,
            value: breed.id,
          })),
          rules: {
            required: true,
          },
          GridProps,
        },
        {
          name: PETS_FIELD_NAMES.weight,
          label: intl.formatMessage({
            id: 'afforda.documentation.applicationSubmission.pets.weight',
          }),
          variant: 'number',
          rules: {
            required: requiresWeight,
          },
          GridProps: { ...GridProps, marginBottom: 2 },
        },
        {
          name: PETS_FIELD_NAMES.serviceAnimal,
          label: intl.formatMessage({
            id: `afforda.documentation.applicationSubmission.pets.serviceAnimal`,
          }),
          variant: 'buttonGroup',
          options: YES_NO_OPTIONS,
          rules: {
            required: true,
          },
          GridProps,
          direction: 'row',
        },
      ].map((field) => ({ ...field, id: formId })),
    [formId, intl, petBreeds, petTypes, requiresWeight],
  );

  return { petsFields, petTypes, petBreeds };
};

export default usePetsFields;
